import React, { memo, useRef, useState, useMemo } from 'react'
import cn from 'classnames'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'

import { BurgerMenuButton } from '../burger-menu-button'
import Text from '../../ui/Text'
import { signOut } from '../sign-out'
import { Drawer } from '../drawer'
import { Link, LinkType } from '../page-header'
import useOnClickOutside from '../../common/hooks/useOnClickOutside'
import { reset } from '../../common/redux/features/user/userSlice'
import useText from '../../common/hooks/useText'

const Wrapper = styled.div`
    font-family: ${({ theme }) => theme.font.name};
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 34px;
    font-size: 1.125rem;
`

const BurgerButton = styled.div`
    width: fit-content;
    height: 20px;
    z-index: 99999;

    &:hover {
        cursor: pointer;
    }

    @media screen and (max-width: 700px) {
        transform: scale(0.8);
    }
`

const SelectedRoute = styled.div`
    font-family: ${({ theme }) => theme.font.name};
    font-weight: 500;
    font-size: 1.25rem;
    letter-spacing: 0.01em;
    color: ${({ theme }) => theme.font.midDarkColor};

    @media screen and (max-width: 700px) {
        display: none;
    }
`

export type HeaderDrawerProps = {
    links: Array<LinkType>
}

const NotificationDot = styled.span`
    width: 8px;
    height: 8px;
    background: ${({ theme }) => theme.brandColors.CA_Error_Red};
    border-radius: 50%;
    display: inline-block;
    margin-left: 6px;
`

const DrawerSubMenu = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 12px 0px 8px 22px;
    font-family: ${({ theme }) => theme.font.name};
    font-weight: 500;
    font-size: 16px;
`

const DrawerMenuItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`

export const PageHeaderDrawer = memo(function HeaderDrawer({ links }: HeaderDrawerProps): React.ReactElement {
    const text = useText()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const [openDrawer, setOpenDrawer] = useState(false)

    const buttonRef = useRef<HTMLButtonElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    useOnClickOutside(containerRef, () => {
        setOpenDrawer(false)
    })

    const callSignOut = () => {
        navigate('login', { replace: true })
        dispatch(reset())
        signOut()
    }

    const selectedRoute = useMemo(() => {
        const directMatch = links.find((l) => l.href === pathname)

        if (directMatch) return directMatch.displayLabelCode || directMatch.labelCode

        for (const link of links) {
            if (link.subMenu) {
                const matchingSubLink = link.subMenu.find((subLink) => pathname.includes(subLink.href.split('/')[1]))
                if (matchingSubLink) {
                    return matchingSubLink.displayLabelCode || matchingSubLink.labelCode
                }
            }
        }

        return null
    }, [pathname, links])

    const isMenuItemSelected = (item: LinkType): boolean => {
        if (!item.href) {
            return false
        }

        if (item.subMenu && item.subMenu.length > 0) {
            const itemBasePath = item.href.split('/')[1]
            const pathParts = pathname.split('/')

            return pathParts.length <= 2 && pathname.includes(itemBasePath)
        }

        return pathname?.includes(item.href.split('/')[1])
    }

    const isSubmenuItemSelected = (href: string): boolean => {
        const subItemPath = href.split('/').filter(Boolean)
        const currentPathParts = pathname.split('/').filter(Boolean)

        if (subItemPath.length > 1) {
            return subItemPath.every((part, index) => currentPathParts[index] === part)
        }

        return (
            pathname.includes(href.split('/')[1]) &&
            (currentPathParts.length === subItemPath.length ||
                (href.split('/')[1] === currentPathParts[0] && currentPathParts.length > 1))
        )
    }

    return (
        <Wrapper ref={containerRef}>
            {selectedRoute ? (
                <SelectedRoute
                    data-testid="location"
                    style={{ visibility: selectedRoute && !openDrawer ? 'visible' : 'hidden' }}
                >
                    <h4>
                        <Text code={selectedRoute} />
                    </h4>
                </SelectedRoute>
            ) : null}
            <BurgerButton onClick={() => setOpenDrawer(!openDrawer)} ref={buttonRef} data-testid="burgerButton">
                <BurgerMenuButton open={openDrawer} />
            </BurgerButton>

            <Drawer className={cn({ open: openDrawer })} data-testid="drawer-menu">
                {links.map((l, i) => (
                    <div key={i}>
                        <DrawerMenuItem>
                            {l.href ? (
                                <Link
                                    as={RouterLink}
                                    to={l.href}
                                    selected={isMenuItemSelected(l)}
                                    data-testid={l.href}
                                    onClick={() => setOpenDrawer(false)}
                                    onMouseEnter={l.prefetch}
                                    light
                                >
                                    <Text code={l.labelCode} />
                                    {l.hasNotification && <NotificationDot />}
                                </Link>
                            ) : (
                                <Link as="div" selected={false} data-testid={`menu-${l.labelCode}`} light>
                                    <Text code={l.labelCode} />
                                    {l.hasNotification && <NotificationDot />}
                                </Link>
                            )}
                        </DrawerMenuItem>

                        {l.subMenu && (
                            <DrawerSubMenu data-testid={`submenu-${l.href || l.labelCode}`}>
                                {l.subMenu.map((subLink, subIndex) => (
                                    <Link
                                        key={subIndex}
                                        as={RouterLink}
                                        to={subLink.href}
                                        light
                                        onClick={() => setOpenDrawer(false)}
                                        selected={isSubmenuItemSelected(subLink.href)}
                                        data-testid={subLink.href}
                                    >
                                        <Text code={subLink.labelCode} />
                                        {subLink.hasNotification && <NotificationDot />}
                                    </Link>
                                ))}
                            </DrawerSubMenu>
                        )}
                    </div>
                ))}

                <Link onClick={callSignOut} data-testid="logOut" selected={false} light>
                    {text('btnLogout')}
                </Link>
            </Drawer>
        </Wrapper>
    )
})
